import { Injectable, signal } from '@angular/core';
import { EventInput } from '@fullcalendar/core';

export type EventStatus = 'confirmed' | 'pending' | 'cancelled';

export interface DemoEvent extends EventInput {
  extendedProps?: { status?: EventStatus; notes?: string };
  color?: string;
  textColor?: string;
}

@Injectable({ providedIn: 'root' })
export class AgendaDemoStore {
  private guid = 0;
  private _events = signal<DemoEvent[]>([]);
  events = this._events.asReadonly();

  constructor() {
    // genera eventos para la semana actual (lunes a viernes)
    this.seedWeekDemo({
      weeks: 1,
      perDayMin: 3,
      perDayMax: 6,
      startHour: 8,
      endHour: 17,
      durations: [30, 45, 60, 90], // minutos
      stepMinutes: 15, // grilla de inicio
      avoidOverlap: true,
    });
  }

  /** API para agregar/actualizar/eliminar si lo necesitas */
  add(e: DemoEvent) {
    this._events.update((arr) => [...arr, { id: this.createId(), ...e }]);
  }
  update(id: string, patch: Partial<DemoEvent>) {
    this._events.update((arr) =>
      arr.map((ev) => (ev.id === id ? { ...ev, ...patch } : ev)),
    );
  }
  remove(id: string) {
    this._events.update((arr) => arr.filter((ev) => ev.id !== id));
  }

  // ----------------- Generador de demo -----------------

  private seedWeekDemo(
    opts: {
      weeks?: number;
      perDayMin?: number;
      perDayMax?: number;
      startHour?: number; // 8
      endHour?: number; // 17
      durations?: number[]; // en minutos
      stepMinutes?: number; // 15
      avoidOverlap?: boolean;
    } = {},
  ) {
    const {
      weeks = 1,
      perDayMin = 3,
      perDayMax = 6,
      startHour = 8,
      endHour = 17,
      durations = [30, 60],
      stepMinutes = 15,
      avoidOverlap = true,
    } = opts;

    const colors = ['#10B981', '#3B82F6', '#7C3AED', '#F59E0B', '#EF4444'];
    const statuses: EventStatus[] = ['confirmed', 'pending', 'cancelled'];

    const startMonday = this.startOfWeekMonday(new Date());
    const out: DemoEvent[] = [];

    for (let w = 0; w < weeks; w++) {
      for (let dayIdx = 0; dayIdx < 5; dayIdx++) {
        // Lunes..Viernes
        const day = new Date(startMonday);
        day.setDate(startMonday.getDate() + w * 7 + dayIdx);

        const count = this.rand(perDayMin, perDayMax);
        const taken: Array<{ s: Date; e: Date }> = [];

        for (let i = 0; i < count; i++) {
          let placed = false;

          // intentos para ubicar un hueco libre
          for (let tries = 0; tries < 60 && !placed; tries++) {
            const dur = this.pick(durations); // 30|45|60|90
            const start = this.randomStart(
              day,
              startHour,
              endHour,
              dur,
              stepMinutes,
            );
            if (!start) break; // no hay hueco posible con esa duración/rango

            const end = new Date(start.getTime() + dur * 60_000);

            if (!avoidOverlap || !this.overlapsAny(start, end, taken)) {
              taken.push({ s: start, e: end });

              const status = this.pick(statuses);
              const color = this.pick(colors);

              out.push({
                id: this.createId(),
                title: this.randomTitle(), // o un servicio/cliente
                start: this.toLocalDateTime(start),
                end: this.toLocalDateTime(end),
                extendedProps: { status },
                color,
                textColor: '#fff',
              });

              placed = true;
            }
          }
        }
      }
    }

    this._events.set(out);
  }

  // ----------------- Utilidades -----------------

  private createId() {
    return String(this.guid++);
  }

  private startOfWeekMonday(d: Date): Date {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = date.getDay(); // 0=Dom, 1=Lun,...6=Sab
    const diff = (day + 6) % 7; // cuántos días retroceder hasta lunes
    date.setDate(date.getDate() - diff);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /** Random start time respetando duración para que termine antes de endHour */
  private randomStart(
    day: Date,
    startHour: number,
    endHour: number,
    durationMin: number,
    stepMin: number,
  ) {
    const startTotal = startHour * 60;
    const latestStart = endHour * 60 - durationMin;
    if (latestStart < startTotal) return null;

    const slots: number[] = [];
    for (let m = startTotal; m <= latestStart; m += stepMin) slots.push(m);

    const chosen = this.pick(slots);
    const h = Math.floor(chosen / 60),
      mm = chosen % 60;

    const d = new Date(day);
    d.setHours(h, mm, 0, 0); // hora local
    return d;
  }

  private overlapsAny(s: Date, e: Date, arr: Array<{ s: Date; e: Date }>) {
    return arr.some((x) => s < x.e && e > x.s);
  }

  private toLocalDateTime(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}:00`;
  }

  private rand(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
  private pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)]!;
  }

  private randomTitle() {
    const names = [
      'María Martínez',
      'Diomedes Díaz',
      'Oscar Melgarejo',
      'Ana Torres',
      'Juan Pérez',
    ];
    const services = ['Corte', 'Consulta', 'Color', 'Sesión', 'Control'];
    return `${this.pick(names)}`;
  }
}
